import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Rating,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { getAllReviews, deleteReview } from '../../firebase/reviews';
import { getProducts } from '../../firebase/products';
import { Review, Product } from '../../types';
import toast from 'react-hot-toast';

const ReviewManager = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [products, setProducts] = useState<Record<string, Product>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [reviewsData, productsData] = await Promise.all([
        getAllReviews(),
        getProducts(),
      ]);
      
      // Convert products array to a map for easier lookup
      const productsMap = productsData.reduce((acc, product) => {
        acc[product.id] = product;
        return acc;
      }, {} as Record<string, Product>);

      setReviews(reviewsData);
      setProducts(productsMap);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setError('Error al cargar las reseñas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (review: Review) => {
    setSelectedReview(review);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedReview(null);
  };

  const handleDeleteReview = async (reviewId: string) => {
    try {
      setDeleting(true);
      await deleteReview(reviewId);
      await loadData();
      toast.success('Reseña eliminada correctamente');
      handleCloseDialog();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error('Error al eliminar la reseña');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Gestionar Reseñas
      </Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Producto</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Valoración</TableCell>
              <TableCell>Comentario</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reviews.map((review) => {
              const product = products[review.productId];
              
              return (
                <TableRow key={review.id}>
                  <TableCell>
                    {product ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {product.images.find(img => img.isPrimary) && (
                          <img
                            src={product.images.find(img => img.isPrimary)?.url}
                            alt={product.name}
                            style={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 4,
                            }}
                          />
                        )}
                        <Typography variant="body2">{product.name}</Typography>
                      </Box>
                    ) : (
                      <Chip label="Producto eliminado" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{review.name}</TableCell>
                  <TableCell>
                    <Rating value={review.rating} readOnly size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {review.comment}
                    </Typography>
                  </TableCell>
                  <TableCell>{review.createdAt.toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(review)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteReview(review.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        {selectedReview && (
          <>
            <DialogTitle>Detalles de la Reseña</DialogTitle>
            <DialogContent>
              <Box sx={{ py: 2 }}>
                {products[selectedReview.productId] && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Producto
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      {products[selectedReview.productId].images.find(img => img.isPrimary) && (
                        <img
                          src={products[selectedReview.productId].images.find(img => img.isPrimary)?.url}
                          alt={products[selectedReview.productId].name}
                          style={{
                            width: 60,
                            height: 60,
                            objectFit: 'cover',
                            borderRadius: 8,
                          }}
                        />
                      )}
                      <Box>
                        <Typography variant="subtitle1">
                          {products[selectedReview.productId].name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {products[selectedReview.productId].price}€
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Usuario
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {selectedReview.name}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Valoración
                  </Typography>
                  <Rating
                    value={selectedReview.rating}
                    readOnly
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Comentario
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {selectedReview.comment}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Fecha
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {selectedReview.createdAt.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cerrar</Button>
              <Button
                color="error"
                onClick={() => handleDeleteReview(selectedReview.id)}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Eliminar Reseña'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

export default ReviewManager;